import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Search, AlertCircle, CheckCircle, XCircle, Phone, Zap, Info } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { CountryPhoneInput } from '@/components/CountryPhoneInput';
import {
  reachabilityService,
  ReachabilityLookup,
  ReachabilityProbe,
} from '@/services/whatsapp-broadcasts.service';
import { templatesService, Template } from '@/services/templates.service';

const STATUS_TONE: Record<string, { tone: string; label: string; icon: React.ReactNode }> = {
  REACHABLE:        { tone: 'text-success', label: 'On WhatsApp',        icon: <CheckCircle size={16} /> },
  NOT_ON_WHATSAPP:  { tone: 'text-danger',  label: 'Not on WhatsApp',    icon: <XCircle size={16} /> },
  BLOCKED_US:       { tone: 'text-warning', label: 'Blocked our number', icon: <AlertCircle size={16} /> },
  UNKNOWN:          { tone: 'text-text-muted', label: 'Unknown',         icon: <AlertCircle size={16} /> },
  PENDING:          { tone: 'text-info',    label: 'Probe sent — waiting on Meta', icon: <Zap size={16} /> },
};

type Props = {
  /** Rendered inside WhatsappToolsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function CheckNumber({ embedded = false }: Props = {}) {
  const [country, setCountry] = useState('AE');
  const [number, setNumber] = useState('');
  const [e164, setE164] = useState('');
  const [lookup, setLookup] = useState<ReachabilityLookup | null>(null);
  const [probe, setProbe] = useState<ReachabilityProbe | null>(null);
  const [looking, setLooking] = useState(false);
  const [probing, setProbing] = useState(false);
  const [showProbeConfirm, setShowProbeConfirm] = useState(false);
  const [utilityTemplates, setUtilityTemplates] = useState<Template[]>([]);
  const [pickedUtilityUid, setPickedUtilityUid] = useState('');

  // Fetch approved utility templates once for the probe template picker.
  useEffect(() => {
    templatesService
      .list({ status: 'APPROVED', category: 'utility', limit: 100 })
      .then((r) => {
        setUtilityTemplates(r.items);
        if (r.items.length > 0) setPickedUtilityUid(r.items[0].templateUid);
      })
      .catch(() => undefined);
  }, []);

  const handleLookup = async () => {
    if (!e164) { toast.error('Enter a valid phone number'); return; }
    setLooking(true);
    setLookup(null);
    setProbe(null);
    try {
      const r = await reachabilityService.lookup(e164, country);
      setLookup(r);
    } catch {
      // interceptor surfaces
    } finally {
      setLooking(false);
    }
  };

  const handleProbe = async () => {
    if (!e164 || !pickedUtilityUid) return;
    setShowProbeConfirm(false);
    setProbing(true);
    try {
      const r = await reachabilityService.probe(e164, country, pickedUtilityUid);
      setProbe(r);
      // Refresh lookup so cache reflects new state.
      const fresh = await reachabilityService.lookup(e164, country);
      setLookup(fresh);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      if (msg) toast.error(msg);
    } finally {
      setProbing(false);
    }
  };

  return (
    <div>
      {!embedded && (
        <Topbar
          title="Check number"
          description="See if a phone number has WhatsApp. Cached lookup is free; live probe sends one billable utility template."
        />
      )}

      <div className={clsx('space-y-5 px-7', embedded ? 'pb-24 pt-5' : 'py-5')}>
        {/* Phone input */}
        <Card padded>
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Phone size={14} /> Phone number
          </h3>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <CountryPhoneInput
                countryCode={country}
                number={number}
                onChange={(v) => { setCountry(v.countryCode); setNumber(v.number); setE164(v.e164); }}
                placeholder="50 123 4567"
              />
            </div>
            <Button
              iconLeft={<Search size={14} />}
              loading={looking}
              disabled={!e164}
              onClick={handleLookup}
            >
              Check cached
            </Button>
          </div>
        </Card>

        {/* Cached result */}
        {lookup && (
          <Card padded>
            <h3 className="text-sm font-semibold text-text-primary mb-3">Cached result</h3>
            {lookup.unknown ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-border bg-surface-alt/50 px-3 py-2.5">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-text-muted" />
                <div className="text-sm">
                  <div className="font-medium text-text-primary">No cached entry</div>
                  <div className="text-text-muted">
                    We&apos;ll learn this number&apos;s reachability the next time you message it.
                    Want to check now? Run a live probe (billable, see below).
                  </div>
                </div>
              </div>
            ) : lookup.cached && (
              <div className="space-y-1">
                <div className={`flex items-center gap-2 text-sm font-semibold ${STATUS_TONE[lookup.cached.status]?.tone ?? ''}`}>
                  {STATUS_TONE[lookup.cached.status]?.icon}
                  {STATUS_TONE[lookup.cached.status]?.label ?? lookup.cached.status}
                </div>
                <div className="text-xs text-text-muted">
                  Last seen {new Date(lookup.cached.lastSeenAt).toLocaleString()}
                  {lookup.cached.lastErrorCode && ` · Error code ${lookup.cached.lastErrorCode}`}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Live probe */}
        {lookup && (
          <Card padded>
            <div className="mb-3 flex items-start gap-2.5">
              <Zap size={16} className="mt-0.5 text-warning" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-text-primary">Live probe (billable)</h3>
                <p className="mt-0.5 text-xs text-text-muted">
                  Sends one approved utility template to this number. The status callback
                  arrives within seconds — REACHABLE if delivered, NOT_ON_WHATSAPP on
                  errors 131026/131047/131049/131051. Charged as a utility conversation.
                  Capped at <strong>5 probes per hour per user</strong>.
                </p>
              </div>
            </div>

            {utilityTemplates.length === 0 ? (
              <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
                <Info size={14} className="mt-0.5 shrink-0 text-warning" />
                <span>
                  You don&apos;t have any approved utility templates yet. Create one in{' '}
                  <a href="/templates" className="font-semibold text-primary hover:underline">Templates</a>{' '}
                  before running a live probe.
                </span>
              </div>
            ) : (
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="mb-1.5 block text-xs font-medium text-text-secondary">Utility template</label>
                  <Select
                    value={pickedUtilityUid}
                    onChange={(e) => setPickedUtilityUid(e.target.value)}
                  >
                    {utilityTemplates.map((t) => (
                      <option key={t.templateUid} value={t.templateUid}>
                        {t.name} ({t.language})
                      </option>
                    ))}
                  </Select>
                </div>
                <Button
                  variant="secondary"
                  iconLeft={<Zap size={14} />}
                  loading={probing}
                  disabled={!pickedUtilityUid || probing}
                  onClick={() => setShowProbeConfirm(true)}
                >
                  Run live probe
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Probe result */}
        {probe && (
          <Card padded>
            <h3 className="text-sm font-semibold text-text-primary mb-3">Probe result</h3>
            <div className={`flex items-center gap-2 text-sm font-semibold ${STATUS_TONE[probe.result]?.tone ?? ''}`}>
              {STATUS_TONE[probe.result]?.icon}
              {STATUS_TONE[probe.result]?.label ?? probe.result}
            </div>
            {probe.result === 'PENDING' && (
              <p className="mt-1 text-xs text-text-muted">
                The dispatch was accepted by Meta. Watch the cache for a final status as the
                webhook callback arrives. We optimistically marked the number as REACHABLE —
                this will downgrade automatically if the recipient bounces.
              </p>
            )}
          </Card>
        )}
      </div>

      {/* Probe confirmation modal */}
      <Modal open={showProbeConfirm} onClose={() => setShowProbeConfirm(false)} title="Confirm live probe">
        <div className="space-y-3 px-6 py-5">
          <p className="text-sm text-text-primary">
            This will send one utility template to <span className="font-mono">{e164}</span>.
          </p>
          <ul className="ml-4 list-disc space-y-1 text-xs text-text-muted">
            <li>You&apos;ll be charged for one utility conversation (varies by country).</li>
            <li>Counts against your daily messaging tier.</li>
            <li>Limit: 5 probes per hour per user.</li>
          </ul>
          <p className="text-xs text-text-muted">
            Don&apos;t use this for bulk list scrubbing — that&apos;s deliberately disallowed.
            See your <a href="https://developers.facebook.com/docs/whatsapp/pricing" target="_blank" rel="noreferrer" className="text-primary hover:underline">Meta pricing matrix</a>.
          </p>
          <div className="-mx-6 -mb-5 mt-4 flex justify-end gap-2 border-t border-border px-6 pt-4 pb-4">
            <Button variant="ghost" onClick={() => setShowProbeConfirm(false)}>Cancel</Button>
            <Button onClick={handleProbe} loading={probing}>Confirm and send</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
